import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useSnapshot } from 'valtio';
import styled from 'styled-components';
import tw from 'twin.macro';
import { authStore } from '../../store/authStore';
import { Modal } from '../components/modal';
import { Button } from '../components/button';
import { getBrand } from '../constants/brands';
import {
  HostAccountStatusDocument,
  IHostAccountStatusQuery,
} from '../services/authService/__generated__/queries.generated';
import {
  HostAccountNotice,
  HostAccountStatus,
  hostAccountNotice,
  hostAccountStatus,
} from '../utils/hostAccountStatus';

type InactiveStatus = Exclude<HostAccountStatus, 'active'>;

export interface HostAccountGuard {
  /**
   * The host account on this brand, from the last server answer (before the first
   * answer, from the signed-in user). Signed out reads as active.
   */
  status: HostAccountStatus;
  /** Words for the notice when the account is not active, else null. */
  notice: HostAccountNotice | null;
  /** Asks the server again (network only). Null when the request failed. */
  refresh: () => Promise<HostAccountStatus | null>;
  /**
   * Re-checks with the server, then runs `action` only when the host is active.
   * Paused or not active: shows the modal instead. A failed request runs the
   * action, so a flaky network never locks a host out. Resolves true when the
   * action ran.
   */
  guard: (action: () => void) => Promise<boolean>;
}

const PASS_THROUGH: HostAccountGuard = {
  status: 'active',
  notice: null,
  refresh: async () => null,
  guard: async action => {
    action();
    return true;
  },
};

const HostAccountGuardContext = createContext<HostAccountGuard>(PASS_THROUGH);

interface FetchedFlags {
  userId: string | null;
  isHost: boolean;
  hostListingsSuspended: boolean;
}

const Title = styled.h2`
  ${tw`text-lg font-semibold text-text-primary m-0 mb-3`}
`;

const Body = styled.p`
  ${tw`text-sm text-text-secondary m-0 mb-3`}
`;

const EmailLink = styled.a`
  ${tw`text-text-primary font-medium underline`}
`;

const Actions = styled.div`
  ${tw`flex justify-end mt-5`}
`;

export function noticeForStatus(status: HostAccountStatus): HostAccountNotice | null {
  return status === 'active' ? null : hostAccountNotice(status, getBrand());
}

/**
 * Paused host (D41). Holds the latest host flags for the site brand and renders the
 * "Host account paused" modal once for every caller of `useHostAccountGuard`.
 */
export function HostAccountGuardProvider({ children }: { children: ReactNode }) {
  const client = useApolloClient();
  const { isAuthenticated, user } = useSnapshot(authStore);
  const userId = user?.id ?? null;
  const [flags, setFlags] = useState<FetchedFlags | null>(null);
  const [shown, setShown] = useState<InactiveStatus | null>(null);

  const current = flags && flags.userId === userId ? flags : null;
  const status: HostAccountStatus = !isAuthenticated
    ? 'active'
    : current
      ? hostAccountStatus(current)
      : user
        ? hostAccountStatus({ isHost: user.isHost, hostListingsSuspended: false })
        : 'active';

  const refresh = useCallback(async (): Promise<HostAccountStatus | null> => {
    if (!authStore.isAuthenticated) return null;
    const forUser = authStore.user?.id ?? null;
    try {
      const { data } = await client.query<IHostAccountStatusQuery>({
        query: HostAccountStatusDocument,
        fetchPolicy: 'network-only',
      });
      const me = data?.getCurrentUser;
      if (!me) return null;
      const next: FetchedFlags = {
        userId: forUser,
        isHost: Boolean(me.isHost),
        hostListingsSuspended: Boolean(me.hostListingsSuspended),
      };
      setFlags(next);
      return hostAccountStatus(next);
    } catch {
      return null;
    }
  }, [client]);

  const guard = useCallback(
    async (action: () => void) => {
      const fresh = await refresh();
      if (fresh && fresh !== 'active') {
        setShown(fresh);
        return false;
      }
      action();
      return true;
    },
    [refresh],
  );

  const value = useMemo<HostAccountGuard>(
    () => ({ status, notice: noticeForStatus(status), refresh, guard }),
    [status, refresh, guard],
  );

  const modalNotice = shown ? hostAccountNotice(shown, getBrand()) : null;
  const close = () => setShown(null);

  return (
    <HostAccountGuardContext.Provider value={value}>
      {children}
      <Modal isOpen={!!modalNotice} onClose={close} title={modalNotice?.title}>
        {modalNotice && (
          <div data-testid="host-account-guard-modal">
            <Title>{modalNotice.title}</Title>
            <Body>{modalNotice.body}</Body>
            {modalNotice.supportEmail && (
              <Body>
                Email{' '}
                <EmailLink href={`mailto:${modalNotice.supportEmail}`}>
                  {modalNotice.supportEmail}
                </EmailLink>
              </Body>
            )}
            <Actions>
              <Button type="button" text="OK" onClick={close} dataTestId="host-account-guard-ok" />
            </Actions>
          </div>
        )}
      </Modal>
    </HostAccountGuardContext.Provider>
  );
}

/** Paused host (D41): the host account status and a guard for hosting actions. */
export function useHostAccountGuard(): HostAccountGuard {
  return useContext(HostAccountGuardContext);
}
